import React, {
  useState,
  useEffect,
  useCallback,
  useMemo,
  Fragment,
} from "react";
import TopBar from "components/topbar";
import { useDispatch } from "react-redux";
import {
  setLoggedOut,
  initiateExternalLogin,
  initiateCodeRequest,
  initiateRequirementsCheck,
  useAuthenticationState,
  buildIdentityString,
} from "redux/features/auth";
import "util/fix-react-bulma.sass";
import {
  Container,
  Box,
  Content,
  Message,
  Button,
  Form,
  Heading,
  Level,
  Element,
} from "react-bulma-components";
import IconButton from "util/iconButton";
import WithTooltip from "util/tooltip";
import {
  FaEnvelope,
  FaSignInAlt,
  FaArrowRight,
  FaArrowLeft,
} from "react-icons/fa";

import TrimmedInput from "common-frontend/util/trimmedInput";
import ErrorMessage from "util/errorMessage";
import { helpAddress } from "config";

import {
  usePromptNewPasswordState,
  PromptNewPassword,
} from "components/accountSettings";

const { Field, Control, Label, Select, Help } = Form;

// Login proceeds in stages; the main Login component itself only renders
// the first stage (DetermineIdentity). Each stage either gathers needed
// information from the user or continues on to render the next stage.

function Login() {
  const { uninitialized, checking, loggingIn } = useAuthenticationState();
  const initializing = uninitialized || checking;
  return (
    <div className="login-screen">
      <TopBar
        authStatus={
          initializing
            ? "Initializing ..."
            : loggingIn
            ? "Signing in ..."
            : "Not logged in"
        }
        loading={initializing || loggingIn}
      />
      <div className="login-body">
        <Container className="is-max-desktop">
          {initializing ? null : (
            <Fragment>
              <Box>
                <DetermineIdentity />
              </Box>
            </Fragment>
          )}
        </Container>
      </div>
    </div>
  );
}

/* STAGE 1: DETERMINATION OF IDENTITY */

// This stage is responsible for setting up an identitySpec object that will
// be used to specify the person's identity. It is an object that consists of
// "identity_type" and "identity" entries.

// This identitySpec object be passed on to subsequent stages, where it will
// be sent to the authentication-requirement-checking endpoint in order to
// determine what credentials and/or other information will need to be
// supplied to log this person in, and then will be sent to the authentication
// endpoint along with this other information in order to actually complete the
// login process.

// Eventually identity can be specified in different ways, such as by a token
// in the URL, so that a person can respond to a personalized link to accept
// an invitation to speak or respond to a request for research output updates.

// For now, however, the only type of identity specification is by e-mail
// address, and the useEmailIdentitySpec hook can be used to build an
// identitySpec object out of an email address.

const IDENTITY_TYPE_EMAIL_ADDRESS = "email_address";

function useEmailIdentitySpec(emailAddress) {
  const identitySpec = useMemo(() => {
    return {
      identity_type: "email_address",
      identity: emailAddress,
    };
  }, [emailAddress]);
  return identitySpec;
}

// The DetermineIdentity component implements this first stage. It requires no
// props. It generates, and passes as props to the next stage once complete,
// the following:

//   * an identitySpec object

//   * a changeIdentity callback, to temporarily block rendering of the next
//     stage and instead offer the user an opportunity to change the identity.
//     This also clears any identity type based off a URL, and switches
//     back to the basic email_address identity type (or prompts for a
//     user-selectable identity type if we ever implement other types of
//     identity that are based on the user inputting something). It allows
//     the user to recover from a defective URL by manually entering their
//     identity instead of having it automatically determined.

function DetermineIdentity() {
  const useEmail = true;
  const [emailAddress, setEmailAddress] = useState("");
  const [emailComplete, setEmailComplete] = useState(false);

  const dispatch = useDispatch();
  const changeIdentity = () => {
    dispatch(setLoggedOut());
    setEmailComplete(false);
    // If we implement other identity types in the future, clear them
    // and reset useEmail to true.
  };

  const validEmail = emailAddress && emailAddress.match(/@[^.]+\.[^.]/);
  const emailIdentitySpec = useEmailIdentitySpec(emailAddress);

  const identitySpec = useEmail && emailComplete ? emailIdentitySpec : null;

  return identitySpec ? (
    <DetermineLoginRequirements {...{ identitySpec, changeIdentity }} />
  ) : (
    <Fragment>
      <Content>
        <Heading>Welcome</Heading>
        <p>
          Here you can register for Fields Institute events and programs, or
          review and update the information we have on file for you.
        </p>
        <p>
          Please start by providing your e-mail address. We'll look up your
          information, and ask for more if you're new to the Institute.
        </p>
      </Content>
      <Field>
        <Label>E-mail Address</Label>
        <Control>
          <TrimmedInput
            type="email"
            value={emailAddress}
            placeholder="you@your.email.domain"
            setValue={setEmailAddress}
          />
        </Control>
      </Field>
      <div className="buttons">
        <WithTooltip
          component={IconButton}
          tipPosition="right"
          tip={
            validEmail
              ? "Continue signing in"
              : "Please enter a valid e-mail address"
          }
          className="login-button"
          color="success"
          onClick={() => setEmailComplete(true)}
          icon={FaArrowRight}
          disabled={!validEmail}
        >
          Continue
        </WithTooltip>
      </div>
    </Fragment>
  );
}

/* STAGE 2: DETERMINATION OF LOGIN REQUIREMENTS */

// This stage uses the identitySpec prop (passed down from the first stage)
// to initiate a requirements check.

// Once the check is complete, it will render whatever subsequent stage is
// meant to come next: password prompting, verification code sending, or
// other information prompting, as per the flow diagram below:

/*

    [ STAGE 2: Determine Login Requirements ] ----------
        |                        \                      \
        | if account with         \ if account has the   \ if no account is
        | password found           \  "login by email     \ set up, but
        |                           \  code" option set,  | identity is a
        V                            \  or there is no    | personalized
     [ STAGE 3a: Prompt for Password] |  account at all   | token sufficient
         |           |                |  and identity is  | for authentication
         | password  | "forgot        |  an email address |
         | entered   | password"      |  that needs       |
         |           | selected       |  verifying        |
         |           V                V                   |
         |        [ STAGE 3b: Send code by email ]        |
         |                            |                   |
         |                            V                   V
         |        [ STAGE 3c: Prompt for non-password login data ]
         |          (e.g.: the code that was sent by email, and/or
         |          new password if none was set or password
         |          reset code was sent, and/or first/last name
         |          if creating new profile
         |                            |
         V                            V
     [ STAGE 4: Attempt login with loginData provided by either ]
         stage 3a or stage 3c as applicable

*/

// DetermineLoginRequirements is the component responsible for stage 2.
// As described above, it takes props identitySpec and changeIdentity.

// Once the requirements have been received from the server, it passes control
// to the appropriate next stage:
//    Stage 3a: the PromptForExistingPassword component,
//    Stage 3b: the SendCodeByEmail component, or
//    Stage 3c: the PromptForCodeOrOtherLoginData component.

// It passes on to the next stage its identitySpec and changeIdentity props
// together with the following two (or 3 in one case*) additional props:

//  "loginRequirements" -- the requirements object returned by the server

//  "codeReason" -- If invoking stages 3a or 3c, this is set to null.
//                  If invoking stage 3b (sending verification code
//                  by email), it is one of the following CODE_REASON_...
//                  constants (except for CODE_REASON_PASSWORD_RESET, as
//                  that is only used when stage 3b is invoked from stage 3a
//                  by the user selecting the "Forgot password" option).

// The codeReason prop is used by stage 3b to determine whether to prompt the
// user or to automatically send the code by e-mail, and if prompting, how
// to word the prompt. It is also used by stage 3c to indicate that the
// user needs to be prompted to enter the code they received.

// *If invoking stage 3c directly: no code required to log in, but no
// password or e-mailed verification code needed either, will also pass a
// "sufficientCredentials" prop containing the necessary credentials to use
// in place of a password or e-mailed code.

const CODE_REASON_PASSWORD_RESET = 1;
const CODE_REASON_PASSWORDLESS_LOGIN = 2;
const CODE_REASON_NO_ACCOUNT = 3;
const CODE_REASON_NEW_PROFILE = 4;

// Note on terminology: a "PROFILE" is a set of information about a person;
// an ACCOUNT is the part of the profile that, once created, holds the
// person's password or passwordless login preference.

// A profile may very well exist without an account, in which case information
// necessary to set the password (or select passwordless login) must be provided
// in order to complete the login and create an account for the profile.

// It is also possible to register for a brand-new profile, in which case
// not only must account setup information be provided, profile setup
// information (first and last name) must be provided too.

// The "signing in" header at the top is a separate component so it
// can be deferred to a later stage, and shown with slightly different
// wording, when creating a new profile.

function SigningInHeader({ identitySpec, changeIdentity, wording }) {
  const { loggingIn } = useAuthenticationState();
  return (
    <Level>
      <Level.Side align="left">
        <Level.Item>
          <span className="signing-in">
            {wording + " "}
            <span className="identity">{identitySpec.identity}</span>.
          </span>
        </Level.Item>
      </Level.Side>

      <Level.Side align="right">
        <Level.Item>
          <Button text onClick={changeIdentity} disabled={loggingIn}>
            Use a different e-mail address instead
          </Button>
        </Level.Item>
      </Level.Side>
    </Level>
  );
}

function DetermineLoginRequirements(props) {
  const { identitySpec, changeIdentity } = props;
  const { requirementsCheck } = useAuthenticationState();
  const dispatch = useDispatch();
  const identityString = buildIdentityString(identitySpec);

  const initiatedCheck =
    requirementsCheck &&
    identityString === requirementsCheck.requirementsIdentityString;
  useEffect(() => {
    if (!initiatedCheck) dispatch(initiateRequirementsCheck(identitySpec));
  }, [initiatedCheck, dispatch, identitySpec]);

  const loginRequirements = initiatedCheck ? requirementsCheck.result : null;
  const checking = initiatedCheck && !requirementsCheck.complete;
  const failure =
    requirementsCheck && requirementsCheck.complete && !requirementsCheck.result
      ? requirementsCheck.failure || {
          errorMessage: "An unknown error occurred",
          errorDetails: {},
        }
      : null;
  const error = failure ? failure.errorMessage : null;

  const usingEmailIdentity =
    identitySpec.identity_type === IDENTITY_TYPE_EMAIL_ADDRESS;

  if (checking || !initiatedCheck) return "Checking";

  if (error)
    return (
      <ErrorMessage>
        <Content>
          <p>There was an error looking up your information: {error}</p>
          <p>
            You may want to{" "}
            <Button text onClick={changeIdentity}>
              try signing in
              {usingEmailIdentity
                ? " with a different e-mail address"
                : " using your e-mail address"}
            </Button>
            .
          </p>
          <p>
            If you continue to experience this error, please contact our staff
            for assistance: {helpAddress}
          </p>
        </Content>
      </ErrorMessage>
    );

  const codeReason = loginRequirements.password
    ? null
    : loginRequirements.code_by_email
    ? CODE_REASON_PASSWORDLESS_LOGIN
    : loginRequirements.no_account
    ? usingEmailIdentity
      ? CODE_REASON_NO_ACCOUNT
      : // no code needed for account setup where person has already entered
        // an individualized identifying code rather than email address
        null
    : loginRequirements.new_profile
    ? CODE_REASON_NEW_PROFILE // New profile being created
    : null;
  const nextStageProps = { ...props, loginRequirements, codeReason };
  const NextComponent = loginRequirements.password
    ? PromptForExistingPassword
    : codeReason
    ? SendCodeByEmail
    : PromptForCodeOrOtherLoginData;

  return (
    <Fragment>
      {identitySpec.identity_type === IDENTITY_TYPE_EMAIL_ADDRESS &&
      !loginRequirements.new_profile ? (
        <SigningInHeader
          {...{ identitySpec, changeIdentity }}
          wording="Signing in as"
        />
      ) : null}

      <NextComponent {...nextStageProps} />
    </Fragment>
  );
}

/* STAGE 3: GATHERING WHATEVER IDENTITY-VALIDATION AND/OR ACCOUNT/PROFILE
            SETUP INFORMATION IS NEEDED FOR LOGIN */

// As described above, the three stage-3 components are
//   PromptForExistingPassword, SendCodeByEmail, PromptForCodeOrOtherLoginData.

// These all take props identitySpec, changeIdentity, loginRequirements, and
// and codeReason. In addition, stage 3b, SendCodeByEmail, also takes a
// retryPassword prop that is set only if it is called via a "forgot password"
// action from stage 3a, allowing the user to go back and try password again
// instead of having a reset code sent. And, stage 3c,
// PromptForCodeOrOtherLoginData, can also take a "sufficientCredentials"
// prop as described above if invoked by stage 2, and a "resendCode" prop
// if invoked from stage 3b.

// When ready, will render the next stage-3 component that is needed, or, if
// all the necessary information has been obtained, will render AttemptLogin
// with props
//     identitySpec,
//     credentials,
//     otherLoginData (account/profile setup information),
//     onFailure (callback for if the login attempt fails)

function PromptForExistingPassword(props) {
  const { identitySpec, loginRequirements } = props;
  const [password, setPassword] = useState("");
  const [forgotPassword, setForgotPassword] = useState(false);
  const [passwordComplete, setPasswordComplete] = useState(false);
  const [errorMessage, setErrorMessage] = useState(null);

  const onContinue = () => {
    setErrorMessage(null);
    setPasswordComplete(true);
  };

  const onForgotPassword = () => {
    setErrorMessage(null);
    setForgotPassword(true);
  };

  const onFailure = (failure) => {
    setPasswordComplete(false);
    setErrorMessage(failure.errorMessage);
  };

  return passwordComplete ? (
    <AttemptLogin
      {...{
        identitySpec,
        credentials: password,
        otherLoginData: {},
        onFailure,
      }}
    />
  ) : forgotPassword ? (
    <SendCodeByEmail
      retryPassword={() => setForgotPassword(false)}
      {...props}
      codeReason={CODE_REASON_PASSWORD_RESET}
    />
  ) : (
    <Fragment>
      {errorMessage ? (
        <ErrorMessage header="Unable to sign in with the password you provided">
          {errorMessage}
        </ErrorMessage>
      ) : null}
      {loginRequirements.multiple &&
      identitySpec.identity_type === IDENTITY_TYPE_EMAIL_ADDRESS ? (
        <Message color="warning">
          <Content>
            <p>
              <b>We have multiple profiles matching your e-mail address.</b>
            </p>
            <p>
              To sign in to a profile where you've set a unique password: enter
              the password you chose.
            </p>
            <p>
              To sign in to a profile that does not have a unique password, use
              the "I forgot my password" link.
            </p>
            <p>
              <i>
                If any of these profiles are actually you (not just other people
                who share your e-mail address), please merge them into yours
                after signing in
              </i>{" "}
              (go to Edit Email Addresses to do so)
            </p>
          </Content>
        </Message>
      ) : null}
      <Content>
        <p>
          We've found your information. You have previously set up a password to
          use when signing in. Please enter that password here.
        </p>
      </Content>
      <Field horizontal>
        <Field.Label>
          <Label>Password:</Label>
        </Field.Label>
        <Field.Body>
          <Field>
            <Control>
              <TrimmedInput
                type="password"
                value={password}
                setValue={setPassword}
              />
            </Control>
          </Field>
        </Field.Body>
      </Field>
      <div className="buttons">
        <IconButton
          className="login-button"
          color="success"
          onClick={onContinue}
          icon={FaArrowRight}
          disabled={password === ""}
        >
          Sign In
        </IconButton>
      </div>
      <Field>
        <Button text onClick={onForgotPassword}>
          I forgot my password
        </Button>
      </Field>
    </Fragment>
  );
}

const reportPasswordlessLogin = (
  <p>
    You have previously set up the option to sign in by having a verification
    code e-mailed to you (instead of using a password),
  </p>
);

function SendCodeByEmail({ retryPassword, ...props }) {
  const { identitySpec, changeIdentity, loginRequirements, codeReason } = props;
  const identityEmail =
    identitySpec.identity_type === IDENTITY_TYPE_EMAIL_ADDRESS
      ? identitySpec.identity
      : null;

  const emailOptions = loginRequirements.email_addresses || [];

  const sendRightAway =
    codeReason === CODE_REASON_PASSWORDLESS_LOGIN &&
    identitySpec.identity_type === IDENTITY_TYPE_EMAIL_ADDRESS;

  const [emailAddress, setEmailAddress] = useState(
    identityEmail ?? emailOptions[0]
  );

  const dispatch = useDispatch();
  const [initiatedSend, setInitiatedSend] = useState(false);
  const initiateSend = useCallback(() => {
    dispatch(initiateCodeRequest(emailAddress));
    setInitiatedSend(true);
  }, [dispatch, setInitiatedSend, emailAddress]);

  useEffect(() => {
    if (sendRightAway && !initiatedSend) initiateSend();
  }, [sendRightAway, initiatedSend, initiateSend]);

  const mechanism = sendRightAway ? null : !emailAddress ? null : (
    <Level>
      <Level.Item>
        <IconButton color="success" onClick={initiateSend} icon={FaEnvelope}>
          Send Code by E-mail
        </IconButton>
      </Level.Item>
      {identityEmail ? null : (
        <Fragment>
          <Level.Item>To:</Level.Item>
          <Level.Item>
            <Field>
              <Control>
                <Select
                  onChange={(e) => setEmailAddress(e.value)}
                  value={emailAddress}
                >
                  {emailOptions.map((option) => (
                    <option value={option} key={option}>
                      {option}
                    </option>
                  ))}
                </Select>
              </Control>
            </Field>
          </Level.Item>
        </Fragment>
      )}
      {retryPassword ? (
        <Level.Item>
          <IconButton color="danger" onClick={retryPassword} icon={FaArrowLeft}>
            Go back and retry password
          </IconButton>
        </Level.Item>
      ) : null}
    </Level>
  );

  const explanation = sendRightAway ? null : codeReason ===
    CODE_REASON_PASSWORD_RESET ? (
    <p>
      When you select "Send Code by Email" below, we will e-mail you a code you
      can use to reset your password
      {loginRequirements.multiple
        ? " (or sign in to a profile that " +
          "doesn't have a unique password set yet)"
        : null}
      .
    </p>
  ) : // Passwordless login with regular e-mail address will result in code
  // being sent automatically, so the case below is for passwordless login
  // based on some other type of identifying code, in which case we need
  // to prompt for which address to send it to:
  codeReason === CODE_REASON_PASSWORDLESS_LOGIN ? (
    <Fragment>
      {reportPasswordlessLogin}
      {emailOptions.length > 0 ? (
        <p>
          When you select "Send Code by Email" below, we will e-mail you the
          code you need.
        </p>
      ) : (
        <p>
          <Element renderAs="span" italic>
            However, we have no e-mail addresses currently associated to your
            profile.
          </Element>{" "}
          You may try{" "}
          <Button text onClick={changeIdentity}>
            signing in with a specific e-mail address
          </Button>
          , or contact {helpAddress} for assistance.
        </p>
      )}
    </Fragment>
  ) : codeReason === CODE_REASON_NO_ACCOUNT ? (
    <Fragment>
      <p>
        This is your first time signing in using this portal, so we will start
        by verifying your e-mail address and then ask you for an optional
        password to use next time you sign in.
      </p>
      <p>
        When you select "Send Code by Email" below, we will send you an e-mail
        to verify your address, with a code to use to complete the sign-in
        process.
      </p>
    </Fragment>
  ) : codeReason === CODE_REASON_NEW_PROFILE &&
    identitySpec.identity_type === IDENTITY_TYPE_EMAIL_ADDRESS ? (
    <Fragment>
      <Heading>Welcome</Heading>
      <p>
        It seems you are new to the Institute (we do not have any record of your
        e-mail address <span className="identity">{identitySpec.identity}</span>
        ). We will create a new profile for you.
      </p>

      <p>
        When you select "Send Code by Email" below, we will send you an e-mail
        to verify your address. Once you've received it, you can enter the code
        it contains, and then we'll ask you for your name and an optional
        password to use next time you sign in.
      </p>
    </Fragment>
  ) : null;

  const footer =
    codeReason === CODE_REASON_NEW_PROFILE &&
    identitySpec.identity_type === IDENTITY_TYPE_EMAIL_ADDRESS ? (
      <Message color="warning">
        <Message.Body>
          <Content>
            <p>
              If you are <b>not</b> new to the Institute, please switch to the
              e-mail address you used last time so we can retrieve your
              information (you can change your e-mail address after that if
              desired):
            </p>
            <p>
              <IconButton
                color="warning"
                onClick={changeIdentity}
                icon={FaArrowLeft}
              >
                Go Back and Change E-mail Address
              </IconButton>
            </p>
          </Content>
        </Message.Body>
      </Message>
    ) : null;

  return initiatedSend ? (
    <PromptForCodeOrOtherLoginData resendCode={initiateSend} {...props} />
  ) : (
    <Fragment>
      <Content>{explanation}</Content>
      {mechanism}
      {footer}
    </Fragment>
  );
}

// PromptForCodeOrOtherLogin has three subcomponents, each with
// associated state.

// usePromptNewPasswordState and PromptNewPassword are defined in
// accountSettings.js, since they are also used when changing password after
// login. The other two states and components are defined below.

function usePromptCodeState() {
  const [code, setCode] = useState("");
  const [codeEntered, setCodeEntered] = useState(false);
  const codeIncomplete =
    code === "" ? "Please enter the code you received by e-mail" : null;
  const codeError = codeEntered ? codeIncomplete : null;

  const codeState = useMemo(() => {
    return {
      code,
      setCode,
      setCodeEntered,
      codeIncomplete,
      codeError,
    };
  }, [code, codeIncomplete, codeError]);

  return codeState;
}

function PromptCode({
  identitySpec,
  codeReason,
  resendCode,
  loggingIn,
  code,
  setCode,
  setCodeEntered,
  codeError,
}) {
  const codeLabel =
    codeReason === CODE_REASON_PASSWORD_RESET
      ? "Password reset code"
      : "Verification code";

  const codeDescription =
    codeReason === CODE_REASON_PASSWORD_RESET
      ? "A password reset code"
      : codeReason === CODE_REASON_PASSWORDLESS_LOGIN
      ? "A verification code"
      : "An e-mail verification code";

  return (
    <div className="other-login-data-prompt">
      <Content>
        <Fragment>
          {codeReason === CODE_REASON_PASSWORDLESS_LOGIN &&
          identitySpec.identity_type === IDENTITY_TYPE_EMAIL_ADDRESS
            ? reportPasswordlessLogin
            : null}
          <p>
            {codeDescription} has been e-mailed to you. Please enter it here
            when you receive it.{" "}
            <Button text onClick={resendCode}>
              Re-send code
            </Button>
          </p>
        </Fragment>
      </Content>
      <Field horizontal className="large-horiz-label">
        <Field.Label>
          <Label>{codeLabel}</Label>
        </Field.Label>
        <Field.Body>
          <Field>
            <Control>
              <TrimmedInput
                {...(codeError ? { color: "danger" } : {})}
                type="text"
                value={code}
                setValue={setCode}
                disabled={!!loggingIn}
                setFinished={setCodeEntered}
              />
            </Control>
            {codeError ? <Help color="danger">{codeError}</Help> : null}
          </Field>
        </Field.Body>
      </Field>
    </div>
  );
}
function usePromptNameState() {
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [lastNameEntered, setLastNameEntered] = useState();

  const nameIncomplete = lastName === "" ? "Please enter your name" : null;
  const nameError = lastNameEntered ? nameIncomplete : null;

  const promptNameState = useMemo(() => {
    return {
      firstName,
      setFirstName,
      lastName,
      setLastName,
      setLastNameEntered,
      nameIncomplete,
      nameError,
      nameLoginData: {
        first_name: firstName,
        last_name: lastName,
        allow_unknown_email: "create",
      },
    };
  }, [firstName, lastName, nameIncomplete, nameError]);
  return promptNameState;
}

function PromptName({
  loggingIn,
  firstName,
  setFirstName,
  lastName,
  setLastName,
  setLastNameEntered,
  nameError,
}) {
  return (
    <div className="other-login-data-prompt">
      <Content>
        <Fragment>
          <p>
            And finally, please tell us your name so we can create a profile for
            you.
          </p>
        </Fragment>
      </Content>

      <Field horizontal className="large-horiz-label">
        <Field.Label>
          <Label>First Name</Label>
        </Field.Label>
        <Field.Body>
          <Field>
            <Control>
              <TrimmedInput
                type="text"
                value={firstName}
                setValue={setFirstName}
                disabled={!!loggingIn}
              />
            </Control>
          </Field>
        </Field.Body>
      </Field>

      <Field horizontal className="large-horiz-label">
        <Field.Label>
          <Label>Last Name</Label>
        </Field.Label>
        <Field.Body>
          <Field>
            <Control>
              <TrimmedInput
                {...(nameError ? { color: "danger" } : {})}
                type="text"
                value={lastName}
                setValue={setLastName}
                disabled={!!loggingIn}
                setFinished={setLastNameEntered}
              />
            </Control>
            {nameError ? <Help color="danger">{nameError}</Help> : null}
          </Field>
        </Field.Body>
      </Field>
    </div>
  );
}

function PromptForCodeOrOtherLoginData(props) {
  const { identitySpec, changeIdentity, loginRequirements, codeReason } = props;
  const { sufficientCredentials, resendCode } = props;

  const { loggingIn } = useAuthenticationState();

  const promptCodeState = usePromptCodeState();
  const promptNewPasswordState = usePromptNewPasswordState();
  const promptNameState = usePromptNameState();

  const [complete, setComplete] = useState(false);
  const [errorMessage, setErrorMessage] = useState(null);

  const onFailure = (failure) => {
    setComplete(false);
    setErrorMessage(failure.errorMessage);
  };

  const header =
    codeReason === CODE_REASON_NEW_PROFILE ? (
      <SigningInHeader
        {...{ identitySpec, changeIdentity }}
        wording="Creating new profile for"
      />
    ) : null;

  const resettingPassword = codeReason === CODE_REASON_PASSWORD_RESET;
  const needNewPassword =
    resettingPassword ||
    (!loginRequirements.password && !loginRequirements.code_by_email);

  const newProfile = loginRequirements.new_profile;

  const { codeIncomplete, code } = promptCodeState;
  const {
    newPasswordIncomplete,
    newPasswordConfirmationIncomplete,
    newPasswordMismatch,
    newPasswordLoginData,
    noPassword,
  } = promptNewPasswordState;

  const { nameIncomplete, nameLoginData } = promptNameState;

  const incomplete =
    (codeReason ? codeIncomplete : null) ??
    (needNewPassword
      ? newPasswordIncomplete ??
        newPasswordConfirmationIncomplete ??
        newPasswordMismatch
      : null) ??
    (newProfile ? nameIncomplete : null);

  const otherLoginData = {
    ...(needNewPassword ? newPasswordLoginData : {}),
    ...(newProfile ? nameLoginData : {}),
  };

  return complete ? (
    <AttemptLogin
      {...{
        identitySpec,
        credentials: codeReason ? code : sufficientCredentials,
        otherLoginData,
        onFailure,
      }}
    />
  ) : (
    <Fragment>
      {errorMessage ? (
        <ErrorMessage header="Unable to Sign In">{errorMessage}</ErrorMessage>
      ) : null}
      {header}
      <div className="other-data-section-container">
        {codeReason ? (
          <PromptCode
            {...{
              identitySpec,
              codeReason,
              resendCode,
              loggingIn,
              ...promptCodeState,
            }}
          />
        ) : null}

        {needNewPassword ? (
          <PromptNewPassword
            {...{ loggingIn, resettingPassword, ...promptNewPasswordState }}
          />
        ) : null}

        {newProfile ? (
          <PromptName {...{ loggingIn, ...promptNameState }} />
        ) : null}
      </div>
      <div className="buttons">
        <WithTooltip
          component={IconButton}
          tipPosition="right"
          tip={incomplete ?? "Sign In"}
          className="login-button"
          color="success"
          onClick={() => {
            setErrorMessage(null);
            setComplete(true);
          }}
          icon={FaSignInAlt}
          disabled={loggingIn || incomplete}
        >
          {newProfile
            ? "Create Profile"
            : needNewPassword
            ? noPassword
              ? "Save Password Settings and Sign In"
              : "Save New Password and Sign In"
            : "Sign In"}
        </WithTooltip>
      </div>
    </Fragment>
  );
}

/* STAGE 4: ATTEMPTING LOGIN */

function AttemptLogin(props) {
  const { identitySpec, credentials, otherLoginData, onFailure } = props;
  const authState = useAuthenticationState();
  const [initiated, setInitiated] = useState(false);
  const dispatch = useDispatch();

  useEffect(() => {
    if (!initiated) {
      setInitiated(true);
      dispatch(
        initiateExternalLogin({
          ...identitySpec,
          credentials,
          ...otherLoginData,
        })
      );
    } else if (authState.authenticationFailure) {
      onFailure(authState.authenticationFailure);
    } else if (!authState.loggingIn) {
      onFailure({
        errorMessage: "An internal error occurred while trying to sign in",
        errorDetails: { unexpectedState: authState },
      });
    }
  }, [
    initiated,
    identitySpec,
    credentials,
    otherLoginData,
    authState,
    dispatch,
    onFailure,
  ]);

  return "Signing in";
}

export default Login;
